import {
  ContentContainer,
  EllipsedText,
  FlexRow,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
  useLoading,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { JOB_TYPE_PAGE } from '../../constants/app';
import { JOB_TYPES_TABLE_COLUMNS_NAME } from '../../constants/jobSkills';
import { useRouting } from '../../hooks/useRouting';
import { useStyles } from '../../hooks/useStyles';
import { getJobTypes } from '../../services/jobTypesService';
import { JobType } from '../../types/jobs';
import { JobTypeModal } from '../jobs/types/JobTypeModal';
import { TableWrapper } from '../shared/TableWrapper';
import StyledTooltip from '../ui/StyledTooltip';

export function JobTypesListPage() {
  const { css, theme, classes } = useStyles();
  const [jobTypes, setJobTypes] = useState<JobType[]>();
  const [selectedType, setSelectedType] = useState<JobType>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number | undefined>(undefined);
  const [totalItems, setTotalItems] = useState(0);
  const { search, setSearch } = useDebouncedSearch(useState<string>());
  const { openModal, closeModal, showModal } = useModal();
  const { goToPage } = useRouting();

  useEffect(() => {
    setPage(0);
  }, [search]);

  useEffect(() => {
    if (page === undefined) return;
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadJobsTypes = async () => {
      const { paginatedResponse, error } = await getJobTypes(page, search);
      if (!error && paginatedResponse) {
        setJobTypes(paginatedResponse.items);
        setTotalItems(paginatedResponse.total);
      } else {
        setJobTypes(undefined);
        setTotalItems(0);
      }
      stopLoading();
    };
    loadJobsTypes();
  }, [page]);

  const onUpdate = () => {
    setPage(0);
  };

  const onJobTypeDetailClick = (jobTypeId: string) => {
    goToPage(generatePath(JOB_TYPE_PAGE.route, { jobTypeId }));
  };

  const onOpenModal = (jobType?: JobType) => {
    setSelectedType(jobType);
    openModal();
  };

  const onSubmitFinished = () => {
    onUpdate();
    onCloseModal();
  };

  const onCloseModal = () => {
    setSelectedType(undefined);
    closeModal();
  };

  return (
    <ContentContainer>
      <FlexRow gap={theme.sizing.scale300}>
        <div className={classes.flexible(1)}>
          <StyledSearchBar
            name="job-type-search"
            value={search}
            onChange={setSearch}
            clearable
            noMargin
          />
        </div>
        <FlexRow
          gap={0}
          classNames={css({ alignItems: 'center' })}
        >
          <StyledTooltip
            content={'Nuevo'}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={() => onOpenModal()}
              kind="tertiary"
            >
              <MaterialIcon name={'add'} />
            </Button>
          </StyledTooltip>
          <StyledTooltip
            content={'Actualizar'}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={onUpdate}
              kind="tertiary"
            >
              <MaterialIcon name={'sync'} />
            </Button>
          </StyledTooltip>
        </FlexRow>
      </FlexRow>
      <TableWrapper
        columns={JOB_TYPES_TABLE_COLUMNS_NAME}
        data={jobTypes?.map((type, i) => {
          return {
            items: [
              <EllipsedText key={`type-name-${i}`}>{type.name}</EllipsedText>,
              <EllipsedText key={`type-cost-${i}`}>
                {type.cost ? `$${Intl.NumberFormat().format(type.cost)}` : '-'}
              </EllipsedText>,
              <EllipsedText key={`type-description-${i}`}>{type.description ?? '-'}</EllipsedText>,
              <EllipsedText key={`type-status-${i}`}>{type.active ? 'Si' : 'No'}</EllipsedText>,
              <FlexRow
                key={`type-actions-${i}`}
                gap={0}
              >
                <StyledTooltip
                  content={'Editar'}
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="circle"
                    onClick={() => onOpenModal(type)}
                    kind="tertiary"
                  >
                    <MaterialIcon
                      name={'edit'}
                      size="xs-mini"
                    />
                  </Button>
                </StyledTooltip>
                <StyledTooltip
                  content={'Detalle'}
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="circle"
                    onClick={() => onJobTypeDetailClick(type._id)}
                    kind="tertiary"
                  >
                    <MaterialIcon
                      name={'visibility'}
                      size="xs-mini"
                    />
                  </Button>
                </StyledTooltip>
              </FlexRow>,
            ],
          };
        })}
        hasData={jobTypes !== undefined}
        isEmpty={!jobTypes?.length}
        loading={loading}
        noDataDescription="Aun no tienes ningun tipo de trabajo"
        onPageChange={setPage}
        page={page ?? 0}
        totalItems={totalItems}
        hasSearch
      />
      <JobTypeModal
        isOpen={showModal}
        onClose={onCloseModal}
        onSubmitFinished={onSubmitFinished}
        jobType={selectedType}
        jobTypeId={selectedType?._id}
      />
    </ContentContainer>
  );
}
